import type {
  CompanyProfilePageData as Data,
  CompanyProfileRouteData,
} from '../../types/company-profile-page';
import { parseVikePageContextDataField } from '../vike/parse-vike-page-context-data-field';
import { normalizeCompanyRecordUrlStrings } from './normalize-company-record-url-strings';
import { buildCompanyProfileCompanyIdCandidates } from './build-company-profile-company-id-candidates';
import { pickCompanyForDerivedSlug } from './company-profile-slug';

const VIKE_PAGE_CONTEXT_SCRIPT_ID = 'vike_pageContext';

function isCompanyProfileNotFoundData(value: unknown): value is Extract<CompanyProfileRouteData, { notFound: true }> {
  if (!value || typeof value !== 'object') return false;
  const v = value as { notFound?: unknown; locale?: unknown; reason?: unknown; routeSlug?: unknown };
  if (v.notFound !== true) return false;
  if (v.locale !== 'en' && v.locale !== 'fi' && v.locale !== 'sv') return false;
  if (v.reason !== 'no_companies_row' && v.reason !== 'no_algolia_profile') return false;
  if (v.httpStatusCode !== 404) return false;
  return typeof v.routeSlug === 'string' && v.routeSlug.trim() !== '';
}

function isCompanyProfileOkPayload(value: unknown): value is Data {
  if (!value || typeof value !== 'object') return false;
  const v = value as { notFound?: unknown; company?: unknown; locale?: unknown };
  if (v.notFound === true) return false;
  if (!v.company || typeof v.company !== 'object') return false;
  const id = (v.company as { objectID?: unknown }).objectID;
  if (typeof id !== 'string' || !id) return false;
  const loc = v.locale;
  return loc === 'en' || loc === 'fi' || loc === 'sv';
}

/**
 * Does this payload belong to the route slug? Brand slugs (`/tyonantaja/terveystalo`) match the
 * company's stored `profileSlug`, or, in the deploy window before generateCompanyScores has
 * written one, the slug derived from its name (same rule as the loader's derived-slug fallback).
 * Old id-shaped slugs still match by objectID. Without the brand-slug cases the client rejected
 * every server payload after hydration and showed "employer not found" on a page the server had
 * just rendered.
 */
function matchesExpectedSlug(data: Data, expectedSlug: string | null | undefined): boolean {
  if (expectedSlug == null || expectedSlug === '') return true;
  const { objectID, profileSlug, companyName } = data.company as { objectID: string; profileSlug?: string; companyName?: unknown };
  if (objectID === expectedSlug) return true;
  if (typeof profileSlug === 'string' && profileSlug === expectedSlug) return true;
  if (buildCompanyProfileCompanyIdCandidates(expectedSlug).includes(objectID)) return true;
  const name = typeof companyName === 'string' ? companyName : objectID;
  return pickCompanyForDerivedSlug([{ objectID, companyName: name, recentJobsPerMonth: 0 }], expectedSlug) !== null;
}

function notFoundMatchesRoute(
  nf: Extract<CompanyProfileRouteData, { notFound: true }>,
  expectedSlug: string | null | undefined,
): boolean {
  if (expectedSlug == null || expectedSlug === '') return true;
  return nf.routeSlug === expectedSlug;
}

/**
 * Picks the first candidate that looks like company-profile `Data` and matches the route slug when known
 * (avoids showing stale serialized data after client-side navigation).
 */
export function resolveCompanyProfilePageData(
  candidates: Array<Data | null | undefined>,
  expectedCompanySlug: string | null | undefined,
): Data | undefined {
  for (const c of candidates) {
    if (!c || !isCompanyProfileOkPayload(c)) continue;
    if (!matchesExpectedSlug(c, expectedCompanySlug)) continue;
    const company = normalizeCompanyRecordUrlStrings(c.company);
    return company === c.company ? c : { ...c, company };
  }
  return undefined;
}

/**
 * Resolves full route data: explicit `notFound` (same slug) wins over ok payloads; then first ok match.
 */
export function resolveCompanyProfileRouteData(
  candidates: Array<CompanyProfileRouteData | Data | null | undefined>,
  expectedCompanySlug: string | null | undefined,
): CompanyProfileRouteData | undefined {
  for (const c of candidates) {
    if (!c || typeof c !== 'object') continue;
    if (isCompanyProfileNotFoundData(c) && notFoundMatchesRoute(c, expectedCompanySlug)) {
      return c;
    }
  }

  const okOnly = candidates.filter((c): c is Data | null | undefined => {
    if (!c || typeof c !== 'object') return true;
    return !isCompanyProfileNotFoundData(c);
  });

  const ok = resolveCompanyProfilePageData(okOnly, expectedCompanySlug);
  if (ok) return { notFound: false, ...ok };
  return undefined;
}

/**
 * Reads `data` from the inlined Vike page-context script in the DOM (same payload as SSR).
 * Use only on the client during hydration when `useData()` is briefly empty.
 */
export function readCompanyProfileDataFromVikePageContextScript(
  expectedCompanySlug: string | null | undefined,
): Data | undefined {
  const full = readCompanyProfileRouteDataFromVikePageContextScript(expectedCompanySlug);
  if (full?.notFound === true) return undefined;
  if (!full || full.notFound !== false) return undefined;
  const { notFound: _n, ...rest } = full;
  return rest as Data;
}

export function readCompanyProfileRouteDataFromVikePageContextScript(
  expectedCompanySlug: string | null | undefined,
): CompanyProfileRouteData | undefined {
  if (typeof document === 'undefined') return undefined;
  const el = document.getElementById(VIKE_PAGE_CONTEXT_SCRIPT_ID);
  const raw = el?.textContent ?? '';
  const unknown = parseVikePageContextDataField(raw);
  return parseCompanyProfileRouteDataFromUnknown(unknown, expectedCompanySlug);
}

export function parseCompanyProfileRouteDataFromUnknown(
  unknown: unknown,
  expectedCompanySlug: string | null | undefined,
): CompanyProfileRouteData | undefined {
  return resolveCompanyProfileRouteData([unknown as CompanyProfileRouteData | Data | null | undefined], expectedCompanySlug);
}
